import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SignatureCanvas, { SignatureCanvasRef } from '../components/SignatureCanvas';
import { getSignatureTokenInfo, submitSignature, SignatureTokenInfoResponse } from '../services/signatureTokenService';
import { dataUrlToFile } from '../utils/fileUtils';
import { prepareSignatureImage } from '../utils/fileUtils';
import { fileToBase64Content } from '../utils/fileUtils';
import './CustomerSignaturePage.css';

type PageState = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

const CANVAS_HEIGHT_PORTRAIT = 220;
const CANVAS_HEIGHT_LANDSCAPE = 300;

const CustomerSignaturePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const signatureRef = useRef<SignatureCanvasRef>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenInfo, setTokenInfo] = useState<SignatureTokenInfoResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: CANVAS_HEIGHT_PORTRAIT });

  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;

    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (width === 0) return;
      const isLandscape = window.innerWidth > window.innerHeight;
      const height = isLandscape ? CANVAS_HEIGHT_LANDSCAPE : CANVAS_HEIGHT_PORTRAIT;
      setCanvasSize({ width: Math.round(width), height });
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [pageState]);

  useEffect(() => {
    if (!token) {
      setErrorMessage(t('customerSignaturePage.invalidLink'));
      setPageState('error');
      return;
    }

    getSignatureTokenInfo(token)
      .then((info) => {
        setTokenInfo(info);
        setPageState('ready');
      })
      .catch(() => {
        setErrorMessage(t('customerSignaturePage.tokenInvalid'));
        setPageState('error');
      });
  }, [token, t]);

  const handleClear = useCallback(() => {
    signatureRef.current?.clear();
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!signatureRef.current || !token) return;

    if (signatureRef.current.isEmpty()) {
      setErrorMessage(t('customerSignaturePage.emptySignature'));
      return;
    }

    setPageState('submitting');
    setErrorMessage('');

    try {
      const dataUrl = signatureRef.current.toDataURL('image/png');
      const file = dataUrlToFile(dataUrl, 'firma_cliente.png');
      const preparedFile = await prepareSignatureImage(file);
      const base64Content = await fileToBase64Content(preparedFile);

      await submitSignature(token, base64Content);
      setPageState('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('customerSignaturePage.submitError');
      setErrorMessage(message);
      setPageState('ready');
    }
  }, [token, t]);

  return (
    <div className="csp-container">
      <div className="csp-card">
        <div className="csp-logo">
          <span className="material-icons csp-logo-icon">engineering</span>
          <span className="csp-logo-text">ProService</span>
        </div>

        {pageState === 'loading' && (
          <div className="csp-state-center">
            <span className="material-icons csp-spin">sync</span>
            <p>{t('customerSignaturePage.loading')}</p>
          </div>
        )}

        {pageState === 'error' && (
          <div className="csp-state-center csp-state-error">
            <span className="material-icons">error_outline</span>
            <p>{errorMessage}</p>
          </div>
        )}

        {pageState === 'success' && (
          <div className="csp-state-center csp-state-success">
            <span className="material-icons">check_circle</span>
            <h2>{t('customerSignaturePage.successTitle')}</h2>
            <p>{t('customerSignaturePage.successMessage')}</p>
          </div>
        )}

        {(pageState === 'ready' || pageState === 'submitting') && tokenInfo && (
          <>
            <div className="csp-report-info">
              <h2>{t('customerSignaturePage.title')}</h2>
              <p className="csp-customer-name">{tokenInfo.customerName}</p>
              <p className="csp-report-number">
                {t('customerSignaturePage.reportNumber')}: <strong>{tokenInfo.reportNumber}</strong>
              </p>
            </div>

            <p className="csp-instructions">{t('customerSignaturePage.instructions')}</p>

            <div className="csp-canvas-wrapper" ref={canvasWrapperRef}>
              <SignatureCanvas
                ref={signatureRef}
                width={canvasSize.width}
                height={canvasSize.height}
                lineWidth={3}
                lineColor="#000000"
                backgroundColor="#ffffff"
                className="csp-canvas"
              />
            </div>

            {errorMessage && (
              <p className="csp-inline-error">{errorMessage}</p>
            )}

            <div className="csp-actions">
              <button
                onClick={handleClear}
                className="csp-btn-clear"
                disabled={pageState === 'submitting'}
              >
                <span className="material-icons">refresh</span>
                {t('signatureModal.clear')}
              </button>
              <button
                onClick={handleSubmit}
                className="csp-btn-confirm"
                disabled={pageState === 'submitting'}
              >
                {pageState === 'submitting' ? (
                  <>
                    <span className="material-icons csp-spin">sync</span>
                    {t('customerSignaturePage.submitting')}
                  </>
                ) : (
                  <>
                    <span className="material-icons">check</span>
                    {t('customerSignaturePage.confirm')}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerSignaturePage;
